import { AxiosError } from "axios";
import { HttpService } from "data/http-service";
import {
  AuthManagement,
  LoginUser,
  UpdateUser,
  User,
  RegisterUser,
  PortailSession,
  ContactEmail,
  CustomerSession,
} from "domain/entities/auth.entities";
import { translate } from "ui/i18n";
import { Snackbar } from "ui/pages/snackbar";
import {
  customerSessionDtoSchema,
  getUserInfosDtoSchema,
  loginDtoSchema,
  portalSessionDtoSchema,
  refreshTokenDtoSchema,
} from "data/auth/auth.dto.schemas";
import {
  removeSessionInLocalStorage,
  setSessionInLocalStorage,
} from "data/auth/auth-session-storage";
import { ParsedUser } from "domain/entities/auth.entities";
import { AuthGateway } from "domain/auth/auth.gateway";

export class AuthHttpGateway implements AuthGateway {
  async register(data: RegisterUser): Promise<{ success: boolean }> {
    return HttpService.post("register", data, {
      api: "connect",
      includeAuthorization: false,
    })
      .then((response) => {
        if (response.status !== 201) {
          Snackbar.showError({
            title: translate("ns1:Register.RegistrationError"),
          });
          return { success: false };
        }
        Snackbar.showSuccess({
          title: translate("ns1:Register.EmailSent"),
        });
        return { success: true };
      })
      .catch((error: AxiosError) => {
        if (error.response.status === 409) {
          Snackbar.showError({
            title: translate("ns1:Register.EmailAlreadyRegistered"),
          });
        } else {
          Snackbar.showError({
            title: translate("ns1:Register.RegistrationError"),
          });
        }
        return { success: false };
      });
  }

  async updateUser(user: UpdateUser): Promise<{ success: boolean }> {
    return HttpService.patch(`users/${user.userId}`, user.data, {
      api: "connect",
      includeAuthorization: false,
    })
      .then((response) => {
        return { success: true };
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Register.UpdateUserError"),
        });
        return { success: false };
      });
  }

  async verifyUser(
    body: AuthManagement
  ): Promise<{ id: number | null; email: string | null }> {
    return HttpService.post("auth-management", body, {
      api: "connect",
      includeAuthorization: false,
    })
      .then((response) => {
        if (response.status === 201) {
          const parsedUser = getUserInfosDtoSchema.safeParse(response.data);
          return { id: parsedUser.data.id, email: parsedUser.data.email };
        } else {
          Snackbar.showError({
            title: translate("ns1:Register.TokenVerificationError"),
          });
          return { id: null, email: null };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Register.TokenVerificationError"),
        });
        return { id: null as number, email: null as string };
      });
  }

  async verifyEmail(body: AuthManagement): Promise<{ emailExists: boolean }> {
    return HttpService.post("auth-management", body, {
      api: "connect",
      includeAuthorization: false,
    })
      .then((response) => {
        return { emailExists: response.status !== 204 };
      })
      .catch((error: AxiosError) => {
        return { emailExists: true };
      });
  }

  async login(user: LoginUser): Promise<{
    userInfos: ParsedUser | null;
    success: boolean;
  }> {
    const errorResponse: {
      userInfos: ParsedUser | null;
      success: boolean;
    } = {
      userInfos: null,
      success: false,
    };
    return HttpService.post(
      "authentication",
      { ...user, strategy: "local" },
      { api: "connect", includeAuthorization: false }
    )
      .then((response) => {
        if (response.status === 201) {
          const parsedUser = loginDtoSchema.safeParse(response.data);
          if (!parsedUser.success) return errorResponse;

          if (parsedUser.data.user.isVerified) {
            setSessionInLocalStorage({
              accessToken: parsedUser.data.accessToken,
              refreshToken: parsedUser.data.refreshToken,
            });
            HttpService.setAccessToken(parsedUser.data.accessToken);
          }
          return {
            userInfos: {
              ...parsedUser.data.user,
              newsletter: !!parsedUser.data.user.newsletter,
            },
            success: true,
          };
        } else return errorResponse;
      })
      .catch((error: AxiosError) => {
        return errorResponse;
      });
  }

  async resendVerifyUser(body: AuthManagement): Promise<{ success: boolean }> {
    return HttpService.post("auth-management", body, {
      api: "connect",
      includeAuthorization: false,
    })
      .then((response) => {
        Snackbar.showSuccess({
          title: translate("ns1:Register.EmailSent"),
        });
        return { success: true };
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Register.ErrorSendingEmail"),
        });
        return { success: false };
      });
  }

  async getUserInfos(): Promise<{
    user: User | null;
    success: boolean;
    refresh?: boolean;
  }> {
    return HttpService.get("me", {
      api: "connect",
    })
      .then((response) => {
        if (response.status === 200) {
          const parsedUser = getUserInfosDtoSchema.safeParse(response.data);
          if (parsedUser.success === false) {
            Snackbar.showError({
              title: translate("ns1:Auth.ErrorGettingUserInfos"),
            });
            return { user: null, success: false };
          } else {
            const user: User = {
              id: parsedUser.data.id,
              email: parsedUser.data.email || "",
              firstName: parsedUser.data.firstName || "",
              lastName: parsedUser.data.lastName || "",
              jobRole: parsedUser.data.jobRole || "",
              newsletter: parsedUser.data.newsletter,
              isPremium: parsedUser.data.isPremium,
              isCustomer: parsedUser.data.isCustomer,
              subscription: parsedUser.data.subscription || undefined,
            };
            return { user, success: true };
          }
        } else {
          Snackbar.showError({
            title: translate("ns1:Auth.ErrorGettingUserInfos"),
          });
        }
      })
      .catch((error: AxiosError) => {
        //if the user is not found, we remove the session and redirect to login page
        if (error.response.status === 404) {
          removeSessionInLocalStorage();
          window.location.href = "/auth";
          Snackbar.showError({
            title: translate("ns1:Auth.ErrorGettingUserInfos"),
          });
        }
        if (error.response.status === 409) {
          return {
            user: null as User,
            success: false,
            refresh: true,
          };
        }

        return { user: null as User, success: false };
      });
  }

  async getPortalSession(): Promise<{
    session: PortailSession | null;
    success: boolean;
  }> {
    return HttpService.get(`stripe/portal-session`, {
      api: "connect",
    })
      .then((response) => {
        if (response.status === 200) {
          const parsedSession = portalSessionDtoSchema.safeParse(response.data);
          if (parsedSession.success) {
            return {
              session: parsedSession.data,
              success: true,
            };
          } else return { session: null, success: false };
        } else {
          return {
            session: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        return { session: null as PortailSession, success: false };
      });
  }

  async sendResetPassword(email: string): Promise<{
    success: boolean;
  }> {
    return HttpService.post(
      "auth-management",
      {
        action: "sendResetPwd",
        value: {
          email,
        },
      },
      {
        api: "connect",
      }
    )
      .then((response) => {
        if (response.status === 201) {
          return { success: true };
        } else return { success: false };
      })
      .catch((error: AxiosError) => {
        return { success: false };
      });
  }

  async sendNewPassword(body: { token: string; password: string }): Promise<{
    success: boolean;
  }> {
    return HttpService.post(
      "auth-management",
      {
        action: "resetPwdLong",
        value: body,
      },
      {
        api: "connect",
      }
    )
      .then((response) => {
        if (response.status === 201) {
          return { success: true };
        } else return { success: false };
      })
      .catch((error: AxiosError) => {
        return { success: false };
      });
  }

  async updatePassword({
    email,
    oldPassword,
    password,
  }: {
    email: string;
    oldPassword: string;
    password: string;
  }): Promise<{
    success: boolean;
  }> {
    return HttpService.post(
      "auth-management",
      {
        action: "passwordChange",
        value: { user: { email }, oldPassword, password },
      },
      {
        api: "connect",
      }
    )
      .then((response) => {
        if (response.status === 201) {
          return { success: true };
        } else {
          Snackbar.showError({
            title: translate("ns1:Account.UpdatePasswordError"),
          });
          return { success: false };
        }
      })
      .catch((error: AxiosError) => {
        return { success: false };
      });
  }

  async updatePersonalInfos({
    data,
    userId,
  }: {
    data: {
      firstName: string;
      lastName: string;
      jobRole: string;
      newsletter: boolean | string;
    };
    userId: number;
  }): Promise<{
    success: boolean;
  }> {
    return HttpService.patch(`users/${userId}`, data, {
      api: "connect",
    })
      .then((response) => {
        if (response.status === 200) {
          return { success: true };
        } else {
          Snackbar.showError({
            title: translate("ns1:Account.UpdateInfosError"),
          });
          return { success: false };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Account.UpdateInfosError"),
        });
        return { success: false };
      });
  }

  async sendContactEmail(body: ContactEmail): Promise<{
    success: boolean;
  }> {
    return HttpService.post(`contact`, body, {
      api: "connect",
    })
      .then((response) => {
        if (response.status === 201) {
          return { success: true };
        } else {
          Snackbar.showError({
            title: translate("ns1:QuoteRequest.ErrorSendingRequest"),
          });
          return { success: false };
        }
      })
      .catch((error: AxiosError) => {
        return { session: null as PortailSession, success: false };
      });
  }

  async getStripeCustomerSession(): Promise<{
    session: CustomerSession | null;
    success: boolean;
  }> {
    return HttpService.get(`stripe/customer-session`, {
      api: "connect",
    })
      .then((response) => {
        if (response.status === 200) {
          const parsedSession = customerSessionDtoSchema.safeParse(
            response.data
          );
          if (parsedSession.success) {
            return {
              session: parsedSession.data,
              success: true,
            };
          } else return { session: null, success: false };
        } else {
          return {
            session: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        return { session: null as CustomerSession, success: false };
      });
  }

  async refreshAuthentication(refreshToken: string): Promise<{
    success: boolean;
  }> {
    return HttpService.post(
      "refresh-authentication",
      {
        refreshToken,
      },
      { api: "connect" }
    ).then((response) => {
      if (response.status === 201) {
        const parsedRefresh = refreshTokenDtoSchema.safeParse(response.data);
        if (!parsedRefresh.success) {
          return { success: false };
        }

        setSessionInLocalStorage({
          accessToken: parsedRefresh.data.accessToken,
          refreshToken: parsedRefresh.data.refreshToken,
        });
        HttpService.setAccessToken(parsedRefresh.data.accessToken);

        return { success: true };
      } else {
        removeSessionInLocalStorage();
        window.location.href = "/auth";

        return { success: false };
      }
    });
  }
}
